/**
 * QuestionEditor — ядро правки одного вопроса (presentation-компоненты).
 *
 * Извлечено из HostEditor.tsx (Фаза 8) для совместного использования хостом
 * (редактор квизов) и админкой (редактор витрин). Чистые presentation-компоненты:
 * принимают props + `onChange`, не зависят от состояния экрана, localStorage или
 * fetch (кроме ImageUpload — он в ./ImageUpload).
 *
 * Состав:
 *   - QuestionCard (export) — карточка: текст, селектор типа, длительность,
 *     картинка (через ImageUpload), блок кода (QuestionCodeSection), превью;
 *     делегирует правки через onChange.
 *   - QuestionBodyEditor — диспетчер switch(body.kind) по 5 типам.
 *   - StringListEditor — редактор списка строк (варианты/accept/items).
 *   - ChoiceForm / PollForm / OpenForm / MatchForm / OrderForm — тип-специфичные формы.
 *   - QuestionCodeSection — сворачиваемая секция «Блок кода» (Фаза 16, B4).
 *   - QuestionPreview — превью через под-view components/views/*.
 *
 * Prop-сигнатуры и data-testid сохранены 1:1 (см. спецификацию Фазы 8 §2.2).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { KeyboardEvent, ReactNode } from 'react';
import {
  draftQuestionToPreview,
  makeDraftBody,
  type DraftQuestion,
  type DraftQuestionBody,
} from '../../drafts';
import { KINDS, type Kind, type QuestionPayload } from '../../protocol';
import type { QuestionBodyProps } from '../views/types';
import { optionLabel } from '../ui-helpers';
import { ChoiceView } from '../views/ChoiceView';
import { PollView } from '../views/PollView';
import { OpenView } from '../views/OpenView';
import { MatchView } from '../views/MatchView';
import { OrderView } from '../views/OrderView';
import { WordView } from '../views/WordView';
import { MultiView } from '../views/MultiView';
import { RunHostView } from '../views/RunHostView';
import { UnknownKindView } from '../views/UnknownKindView';
import { ImageUpload } from './ImageUpload';
import { CodeBlock } from '../CodeBlock';
import { CODE_LANGUAGES, CODE_LANG_PLAINTEXT } from '../../languages';

export interface QuestionCardProps {
  question: DraftQuestion;
  index: number;
  total: number;
  showPreview: boolean;
  onKindChange: (kind: Kind) => void;
  onChange: (q: DraftQuestion) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  /**
   * Лимит длины блока кода (руны) из GET /api/limits → codeMaxChars (Фаза 16,
   * B4); опц. — как questionsLimit у QuizQuestionList: без значения счётчик
   * «N / limit» скрыт и ввод не обрезается (валидирует сервер, 400 limit_chars).
   */
  codeMaxChars?: number;
}

export function QuestionCard({
  question,
  index,
  total,
  showPreview,
  onKindChange,
  onChange,
  onRemove,
  onMove,
  codeMaxChars,
}: QuestionCardProps): JSX.Element {
  const { t } = useTranslation();
  // Defensive: body обязано быть определено (нормализуется в wireQuestionToBody для
  // существующих квизов и в makeDraftQuestion для новых). Страхуемся на случай
  // повреждённого/чужого черновика в localStorage, чтобы один битый вопрос не ронял
  // весь список через question.body.kind. При первой же правке тело заменится на
  // нормальное через onChange (setBody).
  const body: DraftQuestionBody = question.body ?? makeDraftBody('choice');
  const kind = body.kind;

  function setTitle(title: string): void {
    onChange({ ...question, title });
  }
  function setDurationMs(durationMs: string): void {
    const n = Number.parseInt(durationMs, 10);
    // Инпут вводит СЕКУНДЫ, а question.durationMs хранится в МИЛЛИСЕКУНДАХ (см.
    // drafts.ts и draftQuestionToPreview, default 20000мс). Раньше значение писалось
    // как есть → отображение (durationMs/1000) показывало 0, и поле «не работало».
    // Умножаем на 1000: ввод «30» → 30000мс → обратно отображается «30».
    onChange({ ...question, durationMs: Number.isFinite(n) && n > 0 ? n * 1000 : undefined });
  }
  function setBody(nextBody: DraftQuestionBody): void {
    onChange({ ...question, body: nextBody });
  }

  const previewPayload: QuestionPayload = draftQuestionToPreview(
    { ...question, body },
    index,
    total,
  );

  return (
    <li className="question-card">
      <div className="question-card__head">
        <span className="question-card__num">{t('host.editor.questionNum', { n: index + 1 })}</span>
        <span className="q-kind">{t(`kind.${kind}`)}</span>
        <div className="question-card__controls">
          <button
            type="button"
            className="btn btn--ghost"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label={t('host.editor.moveUpAria')}
          >
            ↑
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
            aria-label={t('host.editor.moveDownAria')}
          >
            ↓
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onRemove}
            aria-label={t('host.editor.deleteQuestionAria')}
          >
            {t('common.delete')}
          </button>
        </div>
      </div>

      <div className="form-row">
        <label className="field-label">{t('host.editor.questionText')}</label>
        <input
          className="text-input"
          type="text"
          value={question.title}
          placeholder={t('host.editor.questionPlaceholder')}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="editor-inline-fields">
        <label className="field-label" htmlFor={`kind-${question.id}`}>
          {t('host.editor.type')}
        </label>
        <select
          id={`kind-${question.id}`}
          className="kind-select"
          value={kind}
          onChange={(e) => onKindChange(e.target.value as Kind)}
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`kind.${k}`)}
            </option>
          ))}
        </select>
        <label className="field-label" htmlFor={`dur-${question.id}`}>
          {t('host.editor.seconds')}
        </label>
        <input
          id={`dur-${question.id}`}
          className="text-input editor-dur"
          type="number"
          min={5}
          max={300}
          value={question.durationMs ? Math.round(question.durationMs / 1000) : ''}
          placeholder="20"
          onChange={(e) => setDurationMs(e.target.value)}
        />
      </div>

      <ImageUpload
        image={question.image}
        alt={question.title}
        onImageChange={(url) => onChange({ ...question, image: url || undefined })}
      />

      {/* Блок кода (Фаза 16, B4): сворачиваемая секция сразу после картинки. */}
      <QuestionCodeSection
        question={question}
        codeMaxChars={codeMaxChars}
        onChange={onChange}
      />

      <div className="question-card__body">
        <QuestionBodyEditor body={body} onChange={setBody} />
      </div>

      {showPreview ? (
        <div className="editor-preview" aria-label={t('host.editor.previewAria')}>
          <span className="block-subtitle">{t('host.editor.previewTitle')}</span>
          <QuestionPreview question={previewPayload} />
        </div>
      ) : null}
    </li>
  );
}

// ─── Секция «Блок кода» (Фаза 16, B4) ─────────────────────────────────────────

/**
 * Сворачиваемая секция «Блок кода» в карточке вопроса: моноширинная textarea
 * (Tab вставляет табуляцию, не уводя фокус; Shift+Tab — обратный отступ по
 * строкам выделения), счётчик РУН «N / limits.code_max_chars» (Array.from —
 * не байты и не UTF-16 units), select языка ТОЛЬКО из whitelist-реестра
 * [languages.ts](../../languages.ts), живое превью CodeBlock (B3) и кнопка
 * «Убрать блок» (code+codeLang → undefined).
 *
 * Лимит (prop codeMaxChars из GET /api/limits): ввод сверх лимита обрезается
 * по рунам (превентивно, по образцу maxLength других char-полей редактора —
 * в черновик сверхлимитный код не попадает, серверная 400 limit_chars —
 * страховка); счётчик при достижении лимита подсвечивается
 * (editor-counter--full по образцу счётчика вопросов).
 */
function QuestionCodeSection({
  question,
  codeMaxChars,
  onChange,
}: {
  question: DraftQuestion;
  codeMaxChars?: number;
  onChange: (q: DraftQuestion) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const hasCode = typeof question.code === 'string' && question.code.length > 0;
  // Развёрнута по умолчанию, когда код уже задан (правка существующего вопроса);
  // новый вопрос начинает со свёрнутой секцией — карточка остаётся компактной.
  const [open, setOpen] = useState(hasCode);

  // Число РУН: Array.from итерирует code points — «привет» = 6, эмодзи = 1
  // (не байты и не UTF-16 units; паттерн серверного utf8.RuneCountInString).
  const runes = Array.from(question.code ?? '').length;
  const limit = typeof codeMaxChars === 'number' && codeMaxChars > 0 ? codeMaxChars : 0;

  /** Пишет code в черновик с превентивной обрезкой по рунам до лимита. */
  function setCode(code: string): void {
    let next = code;
    if (limit > 0) {
      const chars = Array.from(next);
      if (chars.length > limit) {
        next = chars.slice(0, limit).join('');
      }
    }
    onChange({ ...question, code: next });
  }

  /** Пишет codeLang; plaintext храним как отсутствие поля (server: пусто => plaintext). */
  function setCodeLang(lang: string): void {
    onChange({ ...question, codeLang: lang === CODE_LANG_PLAINTEXT ? undefined : lang });
  }

  /** «Убрать блок»: code+codeLang → undefined, секция сворачивается. */
  function removeCode(): void {
    onChange({ ...question, code: undefined, codeLang: undefined });
    setOpen(false);
  }

  /**
   * Tab в textarea — вставить символ табуляции в курсор/выделение БЕЗ потери
   * фокуса (preventDefault глушит переход по tab-индексу). Shift+Tab —
   * обратный отступ: снять одну ведущую табуляцию с каждой строки, пересекающей
   * выделение (паттерн редакторов кода; в проекте прецедентов Tab-полей нет —
   * это первое, поведение зафиксировано тестами). DOM-значение обновляется
   * setRangeText (каретка сохраняется), затем синхронизируется state.
   */
  function handleTab(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const ta = e.currentTarget;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    if (e.shiftKey) {
      const value = ta.value;
      const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
      let lineEnd = value.indexOf('\n', end);
      if (lineEnd === -1) lineEnd = value.length;
      const dedented = value
        .slice(lineStart, lineEnd)
        .split('\n')
        .map((line) => (line.startsWith('\t') ? line.slice(1) : line))
        .join('\n');
      ta.setRangeText(dedented, lineStart, lineEnd, 'end');
    } else {
      ta.setRangeText('\t', start, end, 'end');
    }
    setCode(ta.value);
  }

  return (
    <div className="editor-code" data-testid="editor-code-section">
      <button
        type="button"
        className="btn btn--ghost editor-code__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="editor-code-toggle"
      >
        <span aria-hidden="true">{open ? '▾' : '▸'}</span> {t('host.editor.code.section')}
        {hasCode ? (
          <span className="editor-code__badge" data-testid="editor-code-badge">
            {t('host.editor.code.added')}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="editor-code__body">
          <p className="muted small" data-testid="editor-code-hint">
            {t('host.editor.code.hint')}
          </p>
          <textarea
            className="textarea editor-code__textarea"
            rows={10}
            value={question.code ?? ''}
            placeholder={t('host.editor.code.placeholder')}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={handleTab}
            aria-label={t('host.editor.code.section')}
            data-testid="editor-code-textarea"
          />
          <div className="editor-inline-fields">
            <label className="field-label" htmlFor={`code-lang-${question.id}`}>
              {t('host.editor.code.langLabel')}
            </label>
            <select
              id={`code-lang-${question.id}`}
              className="kind-select"
              value={question.codeLang ?? CODE_LANG_PLAINTEXT}
              onChange={(e) => setCodeLang(e.target.value)}
              data-testid="editor-code-lang"
            >
              {CODE_LANGUAGES.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.labelKey ? t(entry.labelKey) : entry.label}
                </option>
              ))}
            </select>
            {limit > 0 ? (
              <span
                className={`muted small editor-counter${runes >= limit ? ' editor-counter--full' : ''}`}
                data-testid="editor-code-counter"
              >
                {t('host.editor.code.counter', { n: runes, limit })}
              </span>
            ) : null}
          </div>
          {hasCode ? (
            <div className="editor-code__preview" data-testid="editor-code-preview">
              <CodeBlock code={question.code ?? ''} lang={question.codeLang} />
            </div>
          ) : null}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={removeCode}
            disabled={!hasCode}
            data-testid="editor-code-remove"
          >
            {t('host.editor.code.remove')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ─── Диспетчер формы по типу вопроса ──────────────────────────────────────────

interface QuestionBodyEditorProps {
  body: DraftQuestionBody;
  onChange: (body: DraftQuestionBody) => void;
}

function QuestionBodyEditor({ body, onChange }: QuestionBodyEditorProps): JSX.Element {
  switch (body.kind) {
    case 'choice':
    case 'multi':
      // multi — разновидность choice в редакторе (чекбокс «Несколько
      // правильных», план C3/§2.6 спеки): одна форма на оба тела.
      return <ChoiceForm body={body} onChange={onChange} />;
    case 'poll':
      return <PollForm body={body} onChange={onChange} />;
    case 'open':
      return <OpenForm body={body} onChange={onChange} />;
    case 'match':
      return <MatchForm body={body} onChange={onChange} />;
    case 'order':
      return <OrderForm body={body} onChange={onChange} />;
    case 'word':
      return <WordForm body={body} onChange={onChange} />;
    case 'run':
      return <RunForm body={body} onChange={onChange} />;
  }
}

// ─── RunForm: «Беги к углу» (§3.5, эпик B4) ────────────────────────────────────

function RunForm({
  body,
  onChange,
}: {
  body: Extract<DraftQuestionBody, { kind: 'run' }>;
  onChange: (b: DraftQuestionBody) => void;
}): JSX.Element {
  const { t } = useTranslation();

  function setCorner(i: number, v: string): void {
    const corners = body.corners.map((c, idx) => (idx === i ? v : c));
    onChange({ kind: 'run', corners, correctIndex: body.correctIndex });
  }

  return (
    <div className="kind-form">
      <label className="field-label">{t('host.editor.runCorners')}</label>
      <div className="string-list">
        {body.corners.map((corner, i) => (
          <div key={i} className="string-list__row">
            <span className="run-corner-pick__label">{optionLabel(i)}</span>
            <input
              className="text-input"
              type="text"
              value={corner}
              placeholder={t('host.editor.optionPlaceholder')}
              maxLength={120}
              onChange={(e) => setCorner(i, e.target.value)}
              data-testid={`editor-run-corner-${i}`}
              aria-label={`${t('host.editor.runCorrect')} ${optionLabel(i)}`}
            />
          </div>
        ))}
      </div>
      <div className="correct-picker">
        <span className="field-label">{t('host.editor.runCorrect')}</span>
        <select
          className="kind-select"
          value={body.correctIndex}
          onChange={(e) =>
            onChange({
              ...body,
              correctIndex: Number.parseInt(e.target.value, 10),
            })
          }
          data-testid="editor-run-correct"
        >
          {body.corners.map((_, i) => (
            <option key={i} value={i}>
              {i + 1}
            </option>
          ))}
        </select>
      </div>
      <p className="muted small" data-testid="editor-run-hint">
        {t('host.editor.runHint')}
      </p>
      <p className="muted small">{t('host.editor.runNoParams')}</p>
    </div>
  );
}

// ─── Строковый список (варианты/accept/items) ─────────────────────────────────

interface StringListEditorProps {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  minItems: number;
  /** Потолок числа элементов (кнопка «+» скрывается при достижении); опц. */
  maxItems?: number;
  /** Доп. содержимое строки (напр., чекбокс «верный» у multi) перед input; опц. */
  renderRowExtra?: (i: number) => ReactNode;
}

function StringListEditor({
  values,
  onChange,
  placeholder,
  minItems,
  maxItems,
  renderRowExtra,
}: StringListEditorProps): JSX.Element {
  const { t } = useTranslation();
  function update(i: number, v: string): void {
    onChange(values.map((x, idx) => (idx === i ? v : x)));
  }
  function add(): void {
    onChange([...values, '']);
  }
  function remove(i: number): void {
    if (values.length <= minItems) return;
    onChange(values.filter((_, idx) => idx !== i));
  }
  const canAdd = maxItems === undefined || values.length < maxItems;
  return (
    <div className="string-list">
      {values.map((v, i) => (
        <div key={i} className="string-list__row">
          {renderRowExtra ? renderRowExtra(i) : null}
          <input
            className="text-input"
            type="text"
            value={v}
            placeholder={placeholder}
            onChange={(e) => update(i, e.target.value)}
          />
          <button
            type="button"
            className="btn btn--ghost string-list__btn"
            onClick={() => remove(i)}
            disabled={values.length <= minItems}
            aria-label={t('host.editor.removeAria')}
          >
            ×
          </button>
        </div>
      ))}
      {canAdd ? (
        <button type="button" className="btn btn--ghost" onClick={add}>
          {t('host.editor.add')}
        </button>
      ) : null}
    </div>
  );
}

// ─── Тип-специфичные формы ────────────────────────────────────────────────────

function ChoiceForm({
  body,
  onChange,
}: {
  /** Единая форма choice/multi: multi получается чекбоксом «Несколько правильных» (§2.6). */
  body: Extract<DraftQuestionBody, { kind: 'choice' | 'multi' }>;
  onChange: (b: DraftQuestionBody) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const isMulti = body.kind === 'multi';

  function setOptions(options: string[]): void {
    if (isMulti) {
      // Удаление варианта убирает его из correct; порядок сохраняется.
      const correct = body.correct.filter((i) => i < options.length);
      onChange({ kind: 'multi', options, correct });
      return;
    }
    let correctIndex = body.correctIndex;
    if (correctIndex >= options.length) correctIndex = options.length - 1;
    if (correctIndex < 0) correctIndex = 0;
    onChange({ kind: 'choice', options, correctIndex });
  }

  /** choice ↔ multi с переносом данных без потерь (план C3, §2.6 спеки). */
  function toggleMulti(on: boolean): void {
    if (on && body.kind === 'choice') {
      // Радио → галочки: единственный верный становится первым отмеченным.
      onChange({ kind: 'multi', options: body.options, correct: [body.correctIndex] });
      return;
    }
    if (!on && body.kind === 'multi') {
      // multi → choice: остаётся ПЕРВЫЙ отмеченный (план C3); опции 1:1.
      const sorted = [...body.correct].sort((a, b) => a - b);
      const first = sorted.length > 0 ? sorted[0] : 0;
      const clamped = Math.min(Math.max(first, 0), body.options.length - 1);
      onChange({ kind: 'choice', options: body.options, correctIndex: clamped });
    }
  }

  function toggleCorrect(i: number): void {
    if (body.kind !== 'multi') return;
    const correct = body.correct.includes(i)
      ? body.correct.filter((x) => x !== i)
      : [...body.correct, i].sort((a, b) => a - b);
    onChange({ kind: 'multi', options: body.options, correct });
  }

  return (
    <div className="kind-form">
      <label className="field-label">{t('host.editor.choiceOptions')}</label>
      <StringListEditor
        values={body.options}
        onChange={setOptions}
        placeholder={t('host.editor.optionPlaceholder')}
        minItems={2}
        maxItems={isMulti ? 8 : undefined}
        renderRowExtra={
          isMulti
            ? (i) => (
                <label className="multi-correct-pick" data-testid={`editor-multi-correct-${i}`}>
                  <input
                    type="checkbox"
                    checked={body.correct.includes(i)}
                    onChange={() => toggleCorrect(i)}
                    aria-label={`${t('host.editor.correctVariant')} ${i + 1}`}
                  />
                </label>
              )
            : undefined
        }
      />
      <div className="editor-inline-fields">
        <label className="multi-toggle" data-testid="editor-multi-toggle">
          <input
            type="checkbox"
            checked={isMulti}
            onChange={(e) => toggleMulti(e.target.checked)}
          />
          <span>{t('host.editor.multiToggle')}</span>
        </label>
      </div>
      {isMulti ? (
        <>
          <p className="muted small" data-testid="editor-multi-hint">
            {t('host.editor.multiToggleHint')}
          </p>
          {body.correct.length === 0 ? (
            <p className="error small" role="alert" data-testid="editor-multi-need-correct">
              {t('host.editor.multiNeedCorrect')}
            </p>
          ) : null}
        </>
      ) : (
        <div className="correct-picker">
          <span className="field-label">{t('host.editor.correctVariant')}</span>
          <select
            className="kind-select"
            value={body.correctIndex}
            onChange={(e) =>
              onChange({ ...body, correctIndex: Number.parseInt(e.target.value, 10) })
            }
          >
            {body.options.map((_, i) => (
              <option key={i} value={i}>
                {i + 1}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function PollForm({
  body,
  onChange,
}: {
  body: Extract<DraftQuestionBody, { kind: 'poll' }>;
  onChange: (b: DraftQuestionBody) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="kind-form">
      <label className="field-label">{t('host.editor.pollOptions')}</label>
      <StringListEditor
        values={body.options}
        onChange={(options) => onChange({ kind: 'poll', options })}
        placeholder={t('host.editor.optionPlaceholder')}
        minItems={2}
      />
    </div>
  );
}

function OpenForm({
  body,
  onChange,
}: {
  body: Extract<DraftQuestionBody, { kind: 'open' }>;
  onChange: (b: DraftQuestionBody) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="kind-form">
      <label className="field-label">{t('host.editor.acceptAnswers')}</label>
      <StringListEditor
        values={body.accept}
        onChange={(accept) => onChange({ kind: 'open', accept, maxLen: body.maxLen })}
        placeholder={t('host.editor.referenceAnswer')}
        minItems={1}
      />
      <div className="editor-inline-fields">
        <label className="field-label" htmlFor={`maxlen-${body.accept.length}`}>
          {t('host.editor.maxLen')}
        </label>
        <input
          id={`maxlen-${body.accept.length}`}
          className="text-input editor-dur"
          type="number"
          min={1}
          max={1000}
          value={body.maxLen ?? ''}
          placeholder={t('host.editor.noLimit')}
          onChange={(e) => {
            const n = Number.parseInt(e.target.value, 10);
            onChange({
              kind: 'open',
              accept: body.accept,
              ...(Number.isFinite(n) && n > 0 ? { maxLen: n } : {}),
            });
          }}
        />
      </div>
    </div>
  );
}

function MatchForm({
  body,
  onChange,
}: {
  body: Extract<DraftQuestionBody, { kind: 'match' }>;
  onChange: (b: DraftQuestionBody) => void;
}): JSX.Element {
  const { t } = useTranslation();
  function setPair(i: number, side: 'left' | 'right', v: string): void {
    const pairs = body.pairs.map((p, idx) => (idx === i ? { ...p, [side]: v } : p));
    onChange({ kind: 'match', pairs });
  }
  function addPair(): void {
    onChange({ kind: 'match', pairs: [...body.pairs, { left: '', right: '' }] });
  }
  function removePair(i: number): void {
    if (body.pairs.length <= 2) return;
    onChange({ kind: 'match', pairs: body.pairs.filter((_, idx) => idx !== i) });
  }
  return (
    <div className="kind-form">
      <label className="field-label">{t('host.editor.matchPairs')}</label>
      <div className="pair-list">
        {body.pairs.map((p, i) => (
          <div key={i} className="pair-list__row">
            <input
              className="text-input"
              type="text"
              value={p.left}
              placeholder={t('host.editor.left')}
              onChange={(e) => setPair(i, 'left', e.target.value)}
            />
            <span className="pair-list__arrow">↔</span>
            <input
              className="text-input"
              type="text"
              value={p.right}
              placeholder={t('host.editor.right')}
              onChange={(e) => setPair(i, 'right', e.target.value)}
            />
            <button
              type="button"
              className="btn btn--ghost string-list__btn"
              onClick={() => removePair(i)}
              disabled={body.pairs.length <= 2}
              aria-label={t('host.editor.deletePairAria')}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="btn btn--ghost" onClick={addPair}>
        {t('host.editor.addPair')}
      </button>
    </div>
  );
}

function WordForm({
  body,
  onChange,
}: {
  body: Extract<DraftQuestionBody, { kind: 'word' }>;
  onChange: (b: DraftQuestionBody) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="kind-form">
      <div className="editor-inline-fields">
        <label className="field-label" htmlFor="word-maxlen">
          {t('word.maxLenLabel')}
        </label>
        <input
          id="word-maxlen"
          className="text-input editor-dur"
          type="number"
          min={1}
          max={200}
          value={body.maxLen ?? ''}
          placeholder="40"
          onChange={(e) => {
            const n = Number.parseInt(e.target.value, 10);
            // Клэмп 1..200 (жёсткий потолок сервера MaxWordLenHardCap);
            // пустое/нечисловое — maxLen не пишем (сервер нормализует в 40).
            onChange({
              kind: 'word',
              ...(Number.isFinite(n) && n > 0 ? { maxLen: Math.min(200, Math.round(n)) } : {}),
            });
          }}
          data-testid="word-maxlen-input"
        />
      </div>
      <p className="muted small">{t('host.editor.wordHint')}</p>
    </div>
  );
}

function OrderForm({
  body,
  onChange,
}: {
  body: Extract<DraftQuestionBody, { kind: 'order' }>;
  onChange: (b: DraftQuestionBody) => void;
}): JSX.Element {
  const { t } = useTranslation();
  // correctOrder[rank-1] = index элемента, стоящего на rank-й позиции верного
  // порядка. В UI ведущий указывает «позицию в верном порядке» (rank) для каждого
  // элемента; по умолчанию rank = i+1 (items уже в верном порядке).
  const rankOf = (idx: number): number => {
    const r = body.correctOrder.indexOf(idx);
    return r < 0 ? idx + 1 : r + 1;
  };
  function setRank(idx: number, rank: number): void {
    const n = body.items.length;
    const clamped = Math.max(1, Math.min(n, Number.isFinite(rank) ? rank : idx + 1));
    // Перестроить correctOrder: расставить прочие элементы вокруг новой позиции.
    const others = body.items
      .map((_, i) => i)
      .filter((i) => i !== idx)
      .sort((a, b) => rankOf(a) - rankOf(b));
    const ordered: number[] = [];
    let placed = false;
    for (const i of others) {
      if (!placed && rankOf(i) > clamped) {
        ordered.push(idx);
        placed = true;
      }
      ordered.push(i);
    }
    if (!placed) ordered.push(idx);
    onChange({ kind: 'order', items: body.items, correctOrder: ordered });
  }
  function setItems(items: string[]): void {
    // При изменении числа элементов пересобираем identity-перестановку.
    const correctOrder = items.map((_, i) => i);
    onChange({ kind: 'order', items, correctOrder });
  }
  return (
    <div className="kind-form">
      <label className="field-label">{t('host.editor.orderItems')}</label>
      <StringListEditor
        values={body.items}
        onChange={setItems}
        placeholder={t('host.editor.item')}
        minItems={2}
      />
      <label className="field-label">{t('host.editor.orderRank')}</label>
      <div className="string-list">
        {body.items.map((_, i) => (
          <div key={i} className="string-list__row">
            <span className="order-rank__label">
              {body.items[i] || t('host.editor.itemN', { n: i + 1 })}
            </span>
            <input
              className="text-input editor-dur"
              type="number"
              min={1}
              max={body.items.length}
              value={rankOf(i)}
              onChange={(e) => setRank(i, Number.parseInt(e.target.value, 10))}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Превью вопроса через под-view (без правильного ответа) ────────────────────

function QuestionPreview({ question }: { question: QuestionPayload }): JSX.Element {
  const bodyProps: QuestionBodyProps = {
    question,
    variant: 'spectate',
    myAnswer: null,
    reveal: null,
    counts: null,
    liveWords: null,
    onAnswer: () => {},
  };
  // Диспетчер превью 1:1 renderBody QuestionView (draft-тела всегда известного
  // kind; default — UnknownKindView для консистентности с §4 спеки Фазы 14).
  const kind = question.kind === undefined || question.kind === '' ? 'choice' : question.kind;
  switch (kind) {
    case 'choice':
      return <ChoiceView {...bodyProps} />;
    case 'poll':
      return <PollView {...bodyProps} />;
    case 'open':
      return <OpenView {...bodyProps} />;
    case 'match':
      return <MatchView {...bodyProps} />;
    case 'order':
      return <OrderView {...bodyProps} />;
    case 'word':
      return <WordView {...bodyProps} />;
    case 'multi':
      return <MultiView {...bodyProps} />;
    case 'run':
      // Превью run — статическая миниатюра плитки (без физики/дронов; полноценный
      // играбельный превью — бэклог, §3.5). kindData без seed → дронов нет.
      return <RunHostView {...bodyProps} />;
    default:
      return <UnknownKindView {...bodyProps} />;
  }
}
